<template>
  <v-dialog
    :value="value"
    width="1200px"
    persistent
  >
    <v-card>
      <v-card-title class="pa-0 sticky">
        <v-toolbar
          dark
          color="primary"
        >
          <v-btn
            dark
            icon
            class="mr-2"
            @click="$emit('input', false)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          Find a Meal Period
          <v-spacer />
          <div v-if="step === 3">
            <v-btn
              light
              color="green"
              class="mx-2"
              @click="step = 2"
            >
              Back
            </v-btn>
            <v-btn
              light
              :color="!selectedMealPeriod ? 'primary darken-1' : 'green'"
              :disabled="!selectedMealPeriod"
              @click="$emit('set-floor-plan-data', selectedLocation, selectedDate, selectedMealPeriod)"
            >
              Finish
            </v-btn>
          </div>
          <div v-else-if="step === 2">
            <v-btn
              light
              color="green"
              class="mx-2"
              @click="step = 1"
            >
              Back
            </v-btn>
            <v-btn
              light
              :color="!selectedDate ? 'primary darken-1' : 'green'"
              :disabled="!selectedDate"
              class="mr-2"
              @click="step = 3"
            >
              Next
            </v-btn>
          </div>
          <div v-else>
            <v-btn
              light
              color="primary darken-1"
              disabled
              class="mx-2"
            >
              Back
            </v-btn>
            <v-btn
              light
              :color="!selectedLocation ? 'primary darken-1' : 'green'"
              :disabled="!selectedLocation"
              class="mr-2"
              @click="step = 2"
            >
              Next
            </v-btn>
          </div>
        </v-toolbar>
      </v-card-title>
      <api-errors :errors="errors" />
      <v-stepper v-model="step">
        <v-stepper-header>
          <v-stepper-step
            :complete="step > 1"
            step="1"
          >
            Select a Location
          </v-stepper-step>
          <v-divider />
          <v-stepper-step
            :complete="step > 2"
            step="2"
          >
            Select a Date
          </v-stepper-step>
          <v-divider />
          <v-stepper-step step="3">
            {{ findEvent ? 'Select an Event' : 'Select a Meal Period' }}
          </v-stepper-step>
        </v-stepper-header>
        <v-stepper-items>
          <v-stepper-content step="1">
            <loading-bar v-if="awaitingApiResponse" />
            <v-data-table
              v-else
              v-model="selectedLocationRaw"
              :headers="locationsTableHeaders"
              :items="locations"
              show-select
              single-select
            >
              <template #header.fast-track>
                <div class="d-flex justify-end">
                  <v-hover v-slot="{ hover }">
                    <v-btn
                      class="mr-2"
                      :color="hover ? 'primary' : 'grey darken-1'"
                      outlined
                      @click="findAnEvent"
                    >
                      Find An Event
                      <v-icon>
                        mdi-chevron-right
                      </v-icon>
                    </v-btn>
                  </v-hover>
                </div>
              </template>
              <template #item="{ item, isSelected }">
                <tr>
                  <td
                    class="thin-cell"
                    @click="selectLocation(item)"
                  >
                    <v-simple-checkbox
                      :value="isSelected"
                      color="primary"
                      :ripple="false"
                      @input="selectLocation(item)"
                    />
                  </td>
                  <td @click="selectLocation(item)">
                    {{ item.name }}
                  </td>
                  <td
                    class="thin-cell"
                    :style="{cursor: 'pointer'}"
                    @click="selectLocation(item, true)"
                  >
                    <v-icon color="primary">
                      mdi-clock-fast
                    </v-icon>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-stepper-content>
          <v-stepper-content step="2">
            <v-row no-gutters>
              <v-spacer />
              <v-col
                cols="8"
                :style="{position: 'relative'}"
              >
                <v-date-picker
                  v-model="selectedDate"
                  :events="d => (calendarConfig.reservationDatesMap || {})[d]"
                  event-color="primary"
                  :min="calendarConfig.min"
                  :max="calendarConfig.max"
                  flat
                  full-width
                  class="pb-2"
                  @change="step += 1"
                />
                <v-progress-circular
                  v-if="loadingReservationDates"
                  indeterminate
                  color="white"
                  :style="{
                    position: 'absolute',
                    top: '12px',
                    right: '12px'
                  }"
                />
              </v-col>
              <v-spacer />
            </v-row>
          </v-stepper-content>
          <v-stepper-content step="3">
            <loading-bar v-if="searchingMealPeriods" />
            <v-data-table
              v-else
              v-model="selectedMealPeriodRaw"
              :headers="finalTableHeaders"
              :items="findEvent ? events : mealPeriods"
              show-select
              single-select
              @click:row="(event) => {
                if (!findEvent) {
                  selectedMealPeriodRaw = !!selectedMealPeriod && selectedMealPeriod.id === event.id ? [] : [event]
                  $emit('set-floor-plan-data', selectedLocation, selectedDate, selectedMealPeriod)
                } else $emit('set-floor-plan-event-data', selectedDate, event)
              }"
            >
              <template #item.data-table-select="{ item, isSelected }">
                <v-simple-checkbox
                  :value="isSelected"
                  color="primary"
                  :ripple="false"
                  @input="() => {
                    if (!findEvent) {
                      selectedMealPeriodRaw = !!selectedMealPeriod && selectedMealPeriod.id === item.id ? [] : [item]
                      $emit('set-floor-plan-data', selectedLocation, selectedDate, selectedMealPeriod)
                    } else $emit('set-floor-plan-event-data', date, item)
                  }"
                />
              </template>
              <template #item.startTime="{ item }">
                {{ displayTime(item.startDateTime) }}
              </template>
              <template #item.endTime="{ item }">
                {{ displayTime(item.endDateTime) }}
              </template>
            </v-data-table>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>
  </v-dialog>
</template>

<script>
import floorPlanHelpers from './mixins/floorPlanHelpers'
import moment from 'moment'
import {helpers} from '@/ft-vue-core'
const {displayTime} = helpers
export default {
  name: 'FindMealPeriod',
  mixins: [floorPlanHelpers],
  props: {
    value: Boolean,
    defaultLocation: Object,
  },
  data () {
    return {
      loadingReservationDates: false,

      findEvent: false,
      events: [],
      mealPeriods: [],
      searchingMealPeriods: false,
      locations: [],
      selectedLocation: undefined,
      selectedDate: undefined,
      selectedMealPeriod: undefined,
      step: 1,
      locationsTableHeaders: [
        this.headerText('Name', 'name', true, undefined, {}),
        this.headerSlot('', 'fast-track', false, {'ft-thin-cell': true}),
      ],
      calendarConfig: {},
    }
  },
  computed: {
    finalTableHeaders () {
      const {headerText: text, headerSlot: slot} = this
      if (this.findEvent) {
        return [
          text('Name', 'name', true),
          slot('Start Time', 'startTime', true),
          slot('End Time', 'endTime', true),
        ]
      } else {
        return [text('Name', 'name', true, undefined, {})]
      }
    },
    formattedSelectedDate () {
      return this.selectedDate ? moment(this.selectedDate).format('ddd, MMM Do') : undefined
    },
    selectedLocationRaw: {
      get () {
        if (this.selectedLocation) return [this.selectedLocation]
        return []
      },
      set (value) {
        this.selectedLocation = value?.[0]
      }
    },
    selectedMealPeriodRaw: {
      get () {
        if (this.selectedMealPeriod) return [this.selectedMealPeriod]
        return []
      },
      set (value) {
        this.selectedMealPeriod = value?.[0]
      }
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.findEvent = false
        this.step = this.defaultStep || 1
        this.selectedLocation = this.defaultLocation
        this.step = this.selectedLocation ? 2 : 1
        this.selectedDate = moment().format('YYYY-MM-DD')
        this.selectedMealPeriod = undefined
        const commands = [
          {
            command: 'diningLocation',
            load: this.locations,
            loading: this.isLoading,
            afterLoad: (r) => {
              this.awaitingApiResponse -= 1
            },
            error: (r) => {
              this.awaitingApiResponse -= 1
              this.errors.push(r.message)
            },
            params: {
              children: true,
              filter_methods: {
                filterByHasAFloorPlan: []
              }
            }
          }
        ]
        this.awaitingApiResponse = commands.length
        this.$ftAPI().get(this.addResourceErrorHandler(commands))
      }
    },
    step (newVal) {
      if (newVal == 1) {
        this.findEvent = false
      } else if (newVal == 2) {
        this.loadingReservationDates = true
        this.$set(this.calendarConfig, 'reservationDatesMap', {})
        let params = {children: true, filter_methods: {filterCancelled: []}}
        if (!this.findEvent) params.filter = [{field: 'locationId', operator: 'EQUAL', values: [this.selectedLocation.id]}]
        this.$ftAPI().get({
          command: this.findEvent ? 'diningEvent' : 'diningReservation',
          load: r => {
            this.$set(this.calendarConfig, 'reservationDatesMap', r.reduce((acc, r) => {
              acc[r.date] = true
              return acc
            }, {}) ?? {})
          },
          afterLoad: () => this.loadingReservationDates = false,
          error: () => this.loadingReservationDates = false,
          params
        })
      } else if (newVal == 3) {
        if (this.findEvent) this.searchEvents()
        else this.searchMealPeriods()
      }
    },
  },
  mounted () {
    this.$ftAPI().get(this.addResourceErrorHandler({
      command: 'diningGetFloorPlanCalendarConfig',
      load: r => {
        let result = r?.[0]
        if (result) {
          let allowedDatesMap = result.mealPeriodDates?.reduce((acc, r) => {
            acc[r] = true
            return acc
          }, {}) ?? {}
          result.allowedDatesMap = allowedDatesMap
        }
        this.calendarConfig = result ?? {}
      }
    }))
  },
  methods: {
    displayTime,
    findAnEvent () {
      this.findEvent = true
      this.step += 1
    },
    selectLocation (location, fastTrack) {
      this.selectedLocationRaw = !!this.selectedLocation?.id === location.id ? [] : [location]
      if (fastTrack) {
        this.awaitingApiResponse = true
        this.$ftAPI().get({
          command: 'diningFindCurrentMealPeriod',
          afterLoad (r) {
            this.awaitingApiResponse = false
            const {location, mealPeriod, dateTime} = r?.[0] ?? {}
            this.$emit('set-floor-plan-data', location, dateTime, mealPeriod)
          },
          error: (e) => {
            this.awaitingApiResponse = false
            this.errors.push(e.message)
          },
          params: {
            filter_methods: {
              filterData: [this.selectedLocation?.id]
            }
          }
        })
        return
      }
      if (!this.selectedLocation?.id !== location.id) this.step += 1
    },
    searchEvents () {
      const {selectedDate} = this
      this.$ftAPI().get({
        command: 'diningEvent',
        load: this.events,
        afterLoad: () => this.searchingMealPeriods = false,
        error: err => {
          this.searchingMealPeriods = false
          this.errors.push(err.message)
        },
        params: {
          children: true,
          filter_methods: {filterCancelled: []},
          filter: [
            {field: 'date', operator: 'EQUAL', values: [selectedDate]},
          ]
        }
      })
    },
    searchMealPeriods () {
      if (this.selectedDate && this.selectedLocation) {
        this.selectedMealPeriod = undefined
        this.mealPeriods = []
        this.searchingMealPeriods = true
        const {selectedDate} = this
        this.$ftAPI().get(this.addResourceErrorHandler({
          command: 'diningMealPeriod',
          load: this.mealPeriods,
          loading: this.isLoading,
          afterLoad: r => this.searchingMealPeriods = false,
          error: r => {
            this.searchingMealPeriods = false
            this.errors.push(r.message)
          },
          params: {
            children: true,
            filter: [
              {field: 'locationId', operator: 'EQUAL', values: [this.selectedLocation.id]},
            ],
            filter_methods: {
              filterWithTargetDate: [selectedDate],
              ...['Future', 'Past', 'Current'].reduce((acc, e) => {
                acc[`includeIs${e}`] = [selectedDate]
                return acc
              }, {})
            },
            include_fields: ['locationName', 'seatings'],
          }
        }))
      }
    }
  }
}
</script>

<style scoped>
.sticky {
  position: sticky;
  top: 0px;
  z-index: 2;
}
.smooth-background {
  transition: background-color 0.2s ease-in-out;
}
.thin-cell {
  width: 0.1%;
  overflow: hidden;
}
</style>
